import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Container, Typography, Card, CardContent, Button, CircularProgress, Alert,
  Chip, TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Table, TableHead, TableBody, TableRow, TableCell,
  IconButton, FormControlLabel, Checkbox, Divider,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import RequireAuth from '../../components/athletes/RequireAuth';
import { useAuth } from '../../context/AuthContext';
import { getTeam, listTeamStaff } from '../../services/teamService';
import {
  listTeamRoster, removeFromRoster, updateRosterDetails,
  generateTeamJoinCode, revokeTeamJoinCode, listActiveTeamJoinCodes,
  revealTeamJoinCode, buildJoinInviteLink,
  listTeamRosterRequests, approveRosterRequest, declineRosterRequest,
} from '../../services/rosterService';
import { FOOTBALL_POSITIONS, formatPositions } from '../../utils/footballPositions';
import { GRADES } from '../../utils/grades';
import TeamHubLayout from '../../components/teams/TeamHubLayout';

function formatDateTime(dateStr) {
  return new Date(dateStr).toLocaleString();
}
function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString();
}

/** Small dialog collecting an optional label before generating a code —
 * "Equipment Handout", "Parent Meeting", etc. Purely descriptive; never
 * required, never touches authorization. */
function GenerateJoinCodeDialog({ open, onClose, onGenerate, generating }) {
  const [label, setLabel] = useState('');

  const handleClose = () => { if (!generating) { setLabel(''); onClose(); } };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onGenerate(label.trim());
    setLabel('');
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Generate Team Join Code</DialogTitle>
      <DialogContent>
        <Typography sx={{ color: '#9eb8d0', fontSize: '0.85rem', mb: 2 }}>
          An optional label helps you tell codes apart later — e.g. "Equipment Handout" or "Parent Meeting". It's just for
          your own reference; it isn't shown to guardians and doesn't change how the code works.
        </Typography>
        <Box component="form" onSubmit={handleSubmit} id="generate-join-code-form">
          <TextField
            fullWidth label="Label (optional)" value={label}
            onChange={e => setLabel(e.target.value)}
            placeholder="Equipment Handout" inputProps={{ maxLength: 100 }}
            autoFocus
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={generating} sx={{ textTransform: 'none', color: '#6a8aa8' }}>Cancel</Button>
        <Button type="submit" form="generate-join-code-form" variant="contained" color="primary" disabled={generating} sx={{ textTransform: 'none', fontWeight: 700 }}>
          {generating ? <CircularProgress size={18} sx={{ color: '#050c16' }} /> : 'Generate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** One row in the Active Join Codes list — label as the primary
 * identifier (falls back to "Join Code"), masked code with eye/Copy/
 * Share Invite, and Revoke. Plaintext is fetched on demand, at most once
 * per row per page load (cached in this row's own local state only —
 * never localStorage/sessionStorage, never a prop passed back up), the
 * first time ANY of eye/Copy/Share Invite is used — not prefetched for
 * every row up front. A code with no code_secret_id (generated before
 *017's reveal support existed) shows a plain "not available" note
 * instead of live controls; no reveal attempt is ever made for it. */
function JoinCodeRow({ code: c, onRevoke, revoking }) {
  const [revealedCode, setRevealedCode] = useState(null);
  const [visible, setVisible] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const [revealError, setRevealError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const canReveal = Boolean(c.code_secret_id);

  const ensureRevealed = async () => {
    if (revealedCode) return revealedCode;
    setRevealing(true);
    setRevealError(null);
    try {
      const plaintext = await revealTeamJoinCode(c.id);
      setRevealedCode(plaintext);
      return plaintext;
    } catch (e) {
      setRevealError(e.message || 'Could not reveal this code.');
      return null;
    } finally {
      setRevealing(false);
    }
  };

  const handleEyeToggle = async () => {
    if (visible) { setVisible(false); return; }
    const plaintext = await ensureRevealed();
    if (plaintext) setVisible(true);
  };

  const handleCopy = async () => {
    const plaintext = await ensureRevealed();
    if (!plaintext) return;
    try {
      await navigator.clipboard.writeText(plaintext);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can fail — the code can still be revealed on
      // screen (eye) and copied by hand, so this is a silent no-op.
    }
  };

  const handleShare = async () => {
    const plaintext = await ensureRevealed();
    if (!plaintext) return;
    const url = buildJoinInviteLink(plaintext);
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Join our team — Football for Nerds', url });
      } catch {
        // Share sheet cancelled or failed — not an error worth surfacing,
        // same as every other clipboard/share edge case on this page.
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      } catch {
        // silent, same convention as handleCopy above
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700 }}>{c.label || 'Join Code'}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5, flexWrap: 'wrap' }}>
          <Typography sx={{ fontFamily: 'monospace', letterSpacing: '0.1em', fontWeight: 700, fontSize: '1.05rem' }}>
            {visible && revealedCode ? revealedCode : '••••••••'}
          </Typography>
          {canReveal ? (
            <>
              <IconButton
                size="small" onClick={handleEyeToggle} disabled={revealing}
                aria-label={visible ? 'Hide code' : 'Show code'} sx={{ color: '#6a8aa8' }}
              >
                {revealing ? <CircularProgress size={14} sx={{ color: '#6a8aa8' }} /> : visible ? '🙈' : '👁'}
              </IconButton>
              <Button size="small" onClick={handleCopy} disabled={revealing} sx={{ textTransform: 'none', color: '#2ea84b', fontWeight: 700, minWidth: 0 }}>
                {copied ? 'Copied ✓' : 'Copy'}
              </Button>
              <Button size="small" onClick={handleShare} disabled={revealing} sx={{ textTransform: 'none', color: '#2ea84b', fontWeight: 700, minWidth: 0 }}>
                {linkCopied ? 'Link Copied ✓' : 'Share Invite'}
              </Button>
            </>
          ) : (
            <Typography sx={{ color: '#4a6080', fontSize: '0.72rem', fontStyle: 'italic' }}>
              Not available to share — created before this feature was added
            </Typography>
          )}
        </Box>
        {revealError && <Typography sx={{ color: '#f44336', fontSize: '0.72rem', mt: 0.25 }}>{revealError}</Typography>}
        <Typography sx={{ color: '#6a8aa8', fontSize: '0.78rem', mt: 0.5 }}>
          Created {formatDate(c.created_at)} · Expires {formatDate(c.expires_at)}
        </Typography>
      </Box>
      <Button
        size="small" variant="outlined" color="secondary" disabled={revoking}
        onClick={() => onRevoke(c.id)} sx={{ textTransform: 'none', fontWeight: 700 }}
      >
        {revoking ? <CircularProgress size={14} sx={{ color: '#6a8aa8' }} /> : 'Revoke'}
      </Button>
    </Box>
  );
}

/** Team Join Code management — the primary V1 onboarding path. A team may
 * have MULTIPLE simultaneously valid join codes (016) — e.g. a separate
 * code for equipment handout, a parent meeting, late registration.
 * Generating a new code never revokes an older one; each code only stops
 * working on its own 14-day expiration or an explicit, per-code revoke.
 * Guardians enter any currently-valid code, choose their athlete, and
 * submit a request that a Team Admin/Head Coach approves below. Team
 * Admin/Head Coach only — the parent page only renders this component at
 * all when canManage is true, and the RPCs independently re-check
 * server-side regardless.
 *
 * The system stores a code's HASH for validation and, separately, a
 * Vault-encrypted copy for later recovery (017) — never plaintext in an
 * ordinary column. A freshly generated code's plaintext is already known
 * client-side (the RPC's own response) and shown immediately; every
 * OTHER listed code's plaintext is fetched on demand, one at a time, only
 * when a coach actually clicks eye/Copy/Share Invite on that row
 * (JoinCodeRow, below) — never prefetched for the whole list. Either way,
 * plaintext only ever lives in React component state for as long as this
 * page stays open — never localStorage/sessionStorage — so leaving or
 * refreshing the page clears it, and revealing it again just re-fetches
 * it through the same authorized RPC call. A code generated before this
 * feature existed has no recoverable plaintext at all (JoinCodeRow shows
 * "not available" for it, never attempts a reveal). Losing/forgetting a
 * code is never a problem either way: generate another one any time
 * without invalidating the others.
 *
 * The other 012 onboarding path — a guardian generating an athlete-
 * specific connection code for direct redemption here — still exists
 * server-side (generate_athlete_connection_code / redeem_athlete_
 * connection_code, untouched) but is intentionally not surfaced in this
 * normal V1 UI per product decision; it remains available as a fallback/
 * admin-recovery tool and a possible future share-link basis. */
function TeamJoinCodeCard({ teamId }) {
  const [activeCodes, setActiveCodes] = useState([]);
  const [generated, setGenerated] = useState(null); // { code, expires_at } — in-memory only
  const [codeRevealed, setCodeRevealed] = useState(true);
  const [copied, setCopied] = useState(false);
  const [generatedLinkCopied, setGeneratedLinkCopied] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [revokingId, setRevokingId] = useState(null);
  const [revokeError, setRevokeError] = useState(null);

  const load = useCallback(async () => {
    const codes = await listActiveTeamJoinCodes(teamId);
    setActiveCodes(codes);
  }, [teamId]);

  useEffect(() => {
    load().catch(e => setError(e.message)).finally(() => setLoading(false));
  }, [load]);

  const handleGenerate = async (label) => {
    setError(null);
    setGenerating(true);
    try {
      const result = await generateTeamJoinCode(teamId, label);
      setGenerated(result);
      setCodeRevealed(true);
      setCopied(false);
      setDialogOpen(false);
      await load();
    } catch (e) {
      setError(e.message || 'Could not generate a team join code.');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!generated) return;
    try {
      await navigator.clipboard.writeText(generated.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can fail (permissions, insecure context) — the
      // code is still visible on screen to copy by hand, so this is a
      // silent no-op rather than a hard error.
    }
  };

  const handleShareGenerated = async () => {
    if (!generated) return;
    const url = buildJoinInviteLink(generated.code);
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Join our team — Football for Nerds', url });
      } catch {
        // Share sheet cancelled or failed — not an error worth surfacing.
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        setGeneratedLinkCopied(true);
        setTimeout(() => setGeneratedLinkCopied(false), 2000);
      } catch {
        // silent, same convention as handleCopy above
      }
    }
  };

  const handleRevoke = async (teamJoinCodeId) => {
    setRevokeError(null);
    setRevokingId(teamJoinCodeId);
    try {
      await revokeTeamJoinCode(teamJoinCodeId);
      await load();
    } catch (e) {
      setRevokeError(e.message || 'Could not revoke that code.');
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a', mb: 3 }}>
      <CardContent>
        <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Team Join Code</Typography>
        <Typography sx={{ color: '#4a6080', fontSize: '0.75rem', mb: 1.5 }}>
          Share a code with families — good for equipment handout, a parent meeting, or late registration. You can have
          several valid codes at once; generating a new one never invalidates the others. Guardians enter a code, choose
          their athlete, and submit a request; entering it never adds anyone directly — you still approve each one below.
        </Typography>

        {loading ? (
          <CircularProgress size={20} sx={{ color: '#2ea84b' }} />
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

            {generated && (
              <Box sx={{ p: 2, border: '1px solid #2ea84b', borderRadius: 2, mb: 1.5 }}>
                <Typography sx={{ fontSize: '0.7rem', color: '#6a8aa8', letterSpacing: '0.05em' }}>NEW TEAM JOIN CODE</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontWeight: 900, fontSize: '1.8rem', letterSpacing: '0.12em', fontFamily: 'monospace' }}>
                    {codeRevealed ? generated.code : '••••••••'}
                  </Typography>
                  <IconButton
                    size="small" onClick={() => setCodeRevealed(v => !v)}
                    aria-label={codeRevealed ? 'Hide code' : 'Show code'} sx={{ color: '#6a8aa8' }}
                  >
                    {codeRevealed ? '🙈' : '👁'}
                  </IconButton>
                  <Button size="small" onClick={handleCopy} sx={{ textTransform: 'none', color: '#2ea84b', fontWeight: 700 }}>
                    {copied ? 'Copied ✓' : 'Copy Code'}
                  </Button>
                  <Button size="small" onClick={handleShareGenerated} sx={{ textTransform: 'none', color: '#2ea84b', fontWeight: 700 }}>
                    {generatedLinkCopied ? 'Link Copied ✓' : 'Share Invite'}
                  </Button>
                </Box>
                <Typography sx={{ color: '#f5a623', fontSize: '0.78rem', mt: 0.5 }}>
                  Expires {formatDateTime(generated.expires_at)}. This exact plaintext view won't be shown again once
                  you leave or refresh this page — but you can still reveal, copy, or share this code later from the
                  list below.
                </Typography>
              </Box>
            )}

            <Button variant="contained" color="primary" onClick={() => setDialogOpen(true)} sx={{ textTransform: 'none', fontWeight: 700, mb: 2 }}>
              + Generate Team Join Code
            </Button>

            <Typography sx={{ fontSize: '0.7rem', color: '#6a8aa8', letterSpacing: '0.05em', fontWeight: 700, mb: 1 }}>
              ACTIVE JOIN CODES {activeCodes.length > 0 && `(${activeCodes.length})`}
            </Typography>

            {revokeError && <Alert severity="error" sx={{ mb: 1.5 }}>{revokeError}</Alert>}

            {activeCodes.length === 0 ? (
              <Typography sx={{ color: '#6a8aa8', fontSize: '0.85rem' }}>
                No active join codes right now — generate one above.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {activeCodes.map((c, idx) => (
                  <Box key={c.id}>
                    {idx > 0 && <Divider sx={{ mb: 1.25 }} />}
                    <JoinCodeRow code={c} onRevoke={handleRevoke} revoking={revokingId === c.id} />
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}
      </CardContent>

      <GenerateJoinCodeDialog
        open={dialogOpen} onClose={() => setDialogOpen(false)} onGenerate={handleGenerate} generating={generating}
      />
    </Card>
  );
}

/** Pending (and recent) roster requests submitted via the Team Join Code
 * path — Team Admin/Head Coach only. Never shows the requesting
 * guardian's identity, only the athlete's own already-appropriate
 * context fields (matches the Roster table above). */
function RosterRequestsCard({ teamId, onChanged }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [actingId, setActingId] = useState(null);
  const [actionError, setActionError] = useState(null);

  const load = useCallback(async () => {
    const r = await listTeamRosterRequests(teamId);
    setRequests(r);
  }, [teamId]);

  useEffect(() => {
    load().catch(e => setLoadError(e.message)).finally(() => setLoading(false));
  }, [load]);

  const pending = requests.filter(r => r.status === 'pending');

  const handleApprove = async (requestId) => {
    setActionError(null);
    setActingId(requestId);
    try {
      await approveRosterRequest(requestId);
      await load();
      await onChanged();
    } catch (e) {
      setActionError(e.message || 'Could not approve that request.');
    } finally {
      setActingId(null);
    }
  };

  const handleDecline = async (requestId) => {
    setActionError(null);
    setActingId(requestId);
    try {
      await declineRosterRequest(requestId);
      await load();
    } catch (e) {
      setActionError(e.message || 'Could not decline that request.');
    } finally {
      setActingId(null);
    }
  };

  if (loading) return null; // avoid a second spinner competing with the page's own

  return (
    <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a', mb: 3 }}>
      <CardContent>
        <Typography sx={{ fontWeight: 800, mb: 1.5 }}>Roster Requests</Typography>
        {loadError && <Alert severity="error" sx={{ mb: 1.5 }}>{loadError}</Alert>}
        {actionError && <Alert severity="error" sx={{ mb: 1.5 }}>{actionError}</Alert>}
        {pending.length === 0 ? (
          <Typography sx={{ color: '#6a8aa8' }}>No pending requests.</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {pending.map((r, idx) => (
              <Box key={r.id}>
                {idx > 0 && <Divider sx={{ mb: 1.5 }} />}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Box>
                    <Typography sx={{ fontWeight: 700 }}>{r.first_name} {r.last_name}</Typography>
                    <Typography sx={{ color: '#6a8aa8', fontSize: '0.78rem' }}>
                      Grade {r.grade || '—'} · {r.school_district_name || 'No school district on file'} · requested {formatDate(r.requested_at)}
                      {/* invite_label (017) is null only for a legacy request with no
                          captured attribution — omitted entirely rather than showing a
                          fabricated placeholder; a genuinely-attributed-but-unlabeled
                          code already resolves server-side to "Join Code". */}
                      {r.invite_label && ` · Invite: ${r.invite_label}`}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small" variant="contained" color="primary" disabled={actingId === r.id}
                      onClick={() => handleApprove(r.id)} sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                      {actingId === r.id ? <CircularProgress size={16} sx={{ color: '#050c16' }} /> : 'Approve'}
                    </Button>
                    <Button
                      size="small" variant="outlined" color="secondary" disabled={actingId === r.id}
                      onClick={() => handleDecline(r.id)} sx={{ textTransform: 'none', fontWeight: 700 }}
                    >
                      Decline
                    </Button>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

/** Roster details management lives here, on the roster (management)
 * page — not on the read-only, coach-facing Rostered Athlete detail
 * page. Phase C.6 — expanded from position-only to also cover Season
 * Grade (the athlete's actual school grade DURING this membership,
 * never a live reference to the athlete's current grade — see
 * update_team_athlete_roster_details(), 047) and Jersey Number
 * (nullable, no uniqueness enforced anywhere; `otherActiveRoster` is
 * only ever used for the advisory duplicate-number note below, never to
 * block saving). All four fields save together as one action, matching
 * the RPC's own "one cohesive edit" contract. */
function EditRosterDetailsDialog({ row, otherActiveRoster, onClose, onSaved }) {
  const [primary, setPrimary] = useState(row?.primary_position || '');
  const [secondary, setSecondary] = useState(row?.secondary_position || '');
  const [seasonGrade, setSeasonGrade] = useState(row?.season_grade || '');
  const [jerseyNumber, setJerseyNumber] = useState(row?.jersey_number ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const duplicateJerseyNames = useMemo(() => {
    if (jerseyNumber === '') return [];
    const n = Number(jerseyNumber);
    if (!Number.isFinite(n)) return [];
    return (otherActiveRoster || [])
      .filter(r => r.id !== row?.id && r.jersey_number === n)
      .map(r => `${r.athlete?.first_name || ''} ${r.athlete?.last_name || ''}`.trim());
  }, [jerseyNumber, otherActiveRoster, row]);

  const handleSave = async (e) => {
    e.preventDefault();
    setError(null);
    if (secondary && secondary === primary) { setError('Secondary position must differ from primary.'); return; }
    if (jerseyNumber !== '' && (Number(jerseyNumber) < 0 || Number(jerseyNumber) > 99)) {
      setError('Jersey number must be between 0 and 99.');
      return;
    }
    setSaving(true);
    try {
      await updateRosterDetails(row.id, {
        primaryPosition: primary,
        secondaryPosition: secondary,
        jerseyNumber: jerseyNumber === '' ? null : Number(jerseyNumber),
        seasonGrade,
      });
      onClose();
      await onSaved();
    } catch (e2) {
      setError(e2.message || 'Could not update this roster record.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={Boolean(row)} onClose={() => !saving && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>{row?.athlete?.first_name} {row?.athlete?.last_name} — Roster Details</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSave} id="edit-roster-details-form" sx={{ display: 'flex', gap: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
          <TextField
            select label="Season Grade" size="small" value={seasonGrade} onChange={e => setSeasonGrade(e.target.value)}
            sx={{ minWidth: 180 }}
            helperText="This athlete's grade for this specific season — kept separately from their profile grade."
          >
            <MenuItem value="">Not set</MenuItem>
            {GRADES.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
          </TextField>
          <TextField
            label="Jersey Number" size="small" type="number" value={jerseyNumber}
            onChange={e => setJerseyNumber(e.target.value)}
            inputProps={{ min: 0, max: 99 }} sx={{ minWidth: 140 }}
          />
          <TextField select label="Primary Position" size="small" value={primary} onChange={e => setPrimary(e.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value="">Not set</MenuItem>
            {FOOTBALL_POSITIONS.map(p => <MenuItem key={p.code} value={p.code}>{p.name} ({p.code})</MenuItem>)}
          </TextField>
          <TextField select label="Secondary Position" size="small" value={secondary} onChange={e => setSecondary(e.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value="">Not set</MenuItem>
            {FOOTBALL_POSITIONS.map(p => <MenuItem key={p.code} value={p.code}>{p.name} ({p.code})</MenuItem>)}
          </TextField>
        </Box>
        {/* Advisory only — duplicate jersey numbers are explicitly
            allowed (youth football can be messy); this never blocks
            saving, it only lets the coach make an informed choice. */}
        {duplicateJerseyNames.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            #{jerseyNumber} is already worn by {duplicateJerseyNames.join(', ')} on this roster. That's allowed — just flagging it.
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none', color: '#6a8aa8' }}>Cancel</Button>
        <Button type="submit" form="edit-roster-details-form" variant="contained" color="primary" disabled={saving} sx={{ textTransform: 'none', fontWeight: 700 }}>
          {saving ? <CircularProgress size={18} sx={{ color: '#050c16' }} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function TeamRosterInner() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [team, setTeam] = useState(null);
  const [roster, setRoster] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [removeError, setRemoveError] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null); // roster row pending confirmation
  const [editingRosterDetails, setEditingRosterDetails] = useState(null); // roster row pending grade/jersey/position edit
  const [showInactive, setShowInactive] = useState(false); // default: active only

  const load = useCallback(async () => {
    const [t, r, s] = await Promise.all([getTeam(teamId), listTeamRoster(teamId), listTeamStaff(teamId)]);
    setTeam(t);
    setRoster(r);
    setStaff(s);
  }, [teamId]);

  useEffect(() => {
    load().catch(e => setError(e.message)).finally(() => setLoading(false));
  }, [load]);

  const myRoles = useMemo(
    () => staff.filter(s => s.user_id === currentUser?.id && s.status === 'active').map(s => s.role),
    [staff, currentUser]
  );
  const canManage = myRoles.includes('team_admin') || myRoles.includes('head_coach');

  // An archived athlete (018) never appears in the CURRENT/operational
  // roster view, even if their team_athletes row is still 'active' — but
  // archiving never touches team_athletes itself (a completely separate
  // concept), so that row, and this athlete's full history, still exist
  // exactly as they did. Such a row belongs in the History view instead
  // (below) — never simply dropped from both, which would make a real,
  // still-active team_athletes row invisible everywhere.
  const activeRoster = useMemo(
    () => roster.filter(r => r.status === 'active' && r.athlete?.status !== 'archived'),
    [roster]
  );
  // Anything NOT on the active roster above: a genuinely removed
  // membership (team_athletes.status !== 'active') OR a membership that's
  // still technically active but whose athlete is archived — the latter
  // is what keeps that valid (active membership, archived athlete) state
  // from disappearing from both views. archive_athlete()/restore_athlete()
  // never touch team_athletes, so this is purely a display classification,
  // not a reflection of any row having actually changed.
  const historyRoster = useMemo(
    () => roster.filter(r => r.status !== 'active' || r.athlete?.status === 'archived'),
    [roster]
  );

  const handleRemoveConfirmed = async () => {
    if (!confirmRemove) return;
    setRemoveError(null);
    setRemovingId(confirmRemove.id);
    try {
      await removeFromRoster(confirmRemove.id);
      setConfirmRemove(null);
      await load();
    } catch (e) {
      setRemoveError(e.message || 'Could not remove that athlete from the roster.');
    } finally {
      setRemovingId(null);
    }
  };

  if (loading) return <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress sx={{ color: '#2ea84b' }} /></Box>;
  if (error) return <Container maxWidth="sm" sx={{ py: 6 }}><Alert severity="error">{error}</Alert></Container>;
  if (!team) return null;

  return (
    <TeamHubLayout team={team} active="roster">
      {removeError && <Alert severity="error" sx={{ mb: 2 }}>{removeError}</Alert>}

      {canManage && (
        <>
          <TeamJoinCodeCard teamId={teamId} />
          <RosterRequestsCard teamId={teamId} onChanged={load} />
        </>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Typography sx={{ fontWeight: 800 }}>Active Roster</Typography>
        {historyRoster.length > 0 && (
          <FormControlLabel
            control={<Checkbox size="small" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />}
            label={<Typography sx={{ fontSize: '0.85rem', color: '#9eb8d0' }}>Show roster history ({historyRoster.length})</Typography>}
          />
        )}
      </Box>

      <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a' }}>
        <CardContent>
          {roster.length === 0 ? (
            <Typography sx={{ color: '#6a8aa8' }}>
              No athletes on this roster yet. {canManage && 'Share your Team Join Code with families above — a guardian enters it, picks their athlete, and you approve the request.'}
            </Typography>
          ) : activeRoster.length === 0 ? (
            <Typography sx={{ color: '#6a8aa8' }}>No active athletes on the roster right now.</Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Grade</TableCell>
                    <TableCell>#</TableCell>
                    <TableCell>Position(s)</TableCell>
                    <TableCell>School District</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">&nbsp;</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeRoster.map(row => (
                    <TableRow
                      key={row.id}
                      hover
                      onClick={() => navigate(`/my-teams/${teamId}/roster/${row.id}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>{row.athlete?.first_name} {row.athlete?.last_name}</TableCell>
                      {/* Phase C.6 — this Team's seasonal grade for this
                          membership (season_grade, 047), never the
                          athlete's own live/current grade — a legacy row
                          with no season_grade yet honestly shows "—"
                          rather than substituting a possibly-wrong
                          current value. */}
                      <TableCell>{row.season_grade || '—'}</TableCell>
                      <TableCell>{row.jersey_number ?? '—'}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <span>{formatPositions(row)}</span>
                          {canManage && (
                            <IconButton
                              size="small" onClick={(e) => { e.stopPropagation(); setEditingRosterDetails(row); }}
                              aria-label="Edit roster details" sx={{ color: '#6a8aa8', p: 0.25 }}
                            >
                              ✎
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{row.athlete?.school_district?.name || '—'}</TableCell>
                      <TableCell>
                        <Chip size="small" label="Active" sx={{ bgcolor: '#0a2a14', color: '#2ea84b', fontWeight: 700 }} />
                      </TableCell>
                      <TableCell align="right">
                        {canManage && (
                          <Button
                            size="small" onClick={(e) => { e.stopPropagation(); setConfirmRemove(row); setRemoveError(null); }}
                            disabled={removingId === row.id}
                            sx={{ textTransform: 'none', color: '#6a8aa8', '&:hover': { color: '#f44336' } }}
                          >
                            Remove
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Roster History — a separate table, never merged into Active Roster
          above. Each row here is EITHER a preserved, past team_athletes
          span (removed_at set — an athlete who may ALSO have a current,
          unrelated active row elsewhere after remove -> rejoin) OR a
          membership that is technically still team_athletes.status =
          'active' but whose athlete is archived (018) — that combination
          is a real, valid state, not a contradiction: archiving never
          touches team_athletes, so the row itself never changed, only
          where it's displayed did. The two are distinguished by the
          Status chip below, never both labeled "Inactive". */}
      {showInactive && historyRoster.length > 0 && (
        <>
          <Typography sx={{ fontWeight: 800, mt: 3, mb: 0.25 }}>Roster History</Typography>
          <Typography sx={{ color: '#4a6080', fontSize: '0.75rem', mb: 1 }}>
            Previous roster periods and archived athletes — preserved for the record, not currently on the active roster above.
          </Typography>
          <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a' }}>
            <CardContent>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Grade</TableCell>
                      <TableCell>#</TableCell>
                      <TableCell>Position(s)</TableCell>
                      <TableCell>Joined</TableCell>
                      <TableCell>Left</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historyRoster.map(row => {
                      // A membership can be technically 'active' in
                      // team_athletes while its athlete is archived — that
                      // is NOT the same fact as the membership having been
                      // removed, so it gets its own distinct label rather
                      // than being folded into "Inactive".
                      const isArchivedButStillRostered = row.status === 'active' && row.athlete?.status === 'archived';
                      return (
                        <TableRow
                          key={row.id}
                          hover
                          onClick={() => navigate(`/my-teams/${teamId}/roster/${row.id}`)}
                          sx={{ cursor: 'pointer', opacity: 0.65 }}
                        >
                          <TableCell sx={{ fontWeight: 600 }}>{row.athlete?.first_name} {row.athlete?.last_name}</TableCell>
                          {/* Phase C.6 — this membership's OWN historical
                              season_grade (047), preserved exactly as it
                              was at the time, regardless of what the
                              athlete's current grade is today. A legacy
                              row backfilled with no honest source shows
                              "—", never a fabricated value. */}
                          <TableCell>{row.season_grade || '—'}</TableCell>
                          <TableCell>{row.jersey_number ?? '—'}</TableCell>
                          <TableCell>{formatPositions(row)}</TableCell>
                          <TableCell>{row.added_at ? formatDate(row.added_at) : '—'}</TableCell>
                          <TableCell>{row.removed_at ? formatDate(row.removed_at) : '—'}</TableCell>
                          <TableCell>
                            {isArchivedButStillRostered ? (
                              <Chip size="small" label="Athlete Archived" sx={{ bgcolor: '#2a1405', color: '#f5a623', fontWeight: 700 }} />
                            ) : (
                              <Chip size="small" label="Inactive" sx={{ bgcolor: '#131318', color: '#6a8aa8', fontWeight: 700 }} />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </CardContent>
          </Card>
        </>
      )}

      <Dialog open={Boolean(confirmRemove)} onClose={() => !removingId && setConfirmRemove(null)}>
        <DialogTitle>
          Remove {confirmRemove?.athlete?.first_name} {confirmRemove?.athlete?.last_name} from the roster?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#6a8aa8', fontSize: '0.9rem' }}>
            This makes their roster membership inactive — it doesn't delete their athlete profile, guardian relationships, or
            measurement history. They can rejoin later once a guardian submits a new Team Join Code request that you approve.
          </Typography>
          {removeError && <Alert severity="error" sx={{ mt: 2 }}>{removeError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRemove(null)} disabled={Boolean(removingId)} sx={{ textTransform: 'none', color: '#6a8aa8' }}>
            Cancel
          </Button>
          <Button onClick={handleRemoveConfirmed} disabled={Boolean(removingId)} variant="contained" color="secondary" sx={{ textTransform: 'none', fontWeight: 700 }}>
            {removingId ? <CircularProgress size={18} sx={{ color: '#050c16' }} /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {editingRosterDetails && (
        <EditRosterDetailsDialog
          row={editingRosterDetails} otherActiveRoster={activeRoster}
          onClose={() => setEditingRosterDetails(null)} onSaved={load}
        />
      )}
    </TeamHubLayout>
  );
}

export default function TeamRosterPage() {
  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Navbar />
      <RequireAuth><TeamRosterInner /></RequireAuth>
    </Box>
  );
}
